import { supabaseClient, hasSupabaseEnv } from './supabase';

export interface LocationInfo {
  name: string;
  county: string;
  type: 'city' | 'county' | 'state';
}

export interface PopulationDataRow {
  location_name: string;
  county: string;
  population: number;
  year: number;
}

export interface LatestPopulationData {
  population: number;
  year: number;
}

// Dev fallback when Supabase is missing or fails (e.g. no .env on localhost)
const DEV_FALLBACK_LOCATIONS: LocationInfo[] = [
  { name: 'Berkeley', county: 'Alameda', type: 'city' }
];
/** Dev-only; production uses population.geofips. Berkeley Census place GEOID is 0606000 → often stored as 606000. */
const DEV_FALLBACK_GEOFIPS: Record<string, number> = { Berkeley: 606000 };

class SupabaseLocationService {
  private locations: LocationInfo[] = [];
  private cityCountyMap: Map<string, string> = new Map();
  private slugMap: Map<string, LocationInfo> = new Map();
  private devFallback = false;
  private initialized = false;

  private useDevFallback() {
    this.locations = [...DEV_FALLBACK_LOCATIONS];
    this.slugMap.clear();
    this.cityCountyMap.clear();
    DEV_FALLBACK_LOCATIONS.forEach((loc) => {
      this.cityCountyMap.set(loc.name, loc.county);
      const slug = this.buildLocationSlug(loc);
      this.slugMap.set(slug, loc);
    });
    this.devFallback = true;
  }

  async initialize() {
    if (this.initialized) {
      return;
    }
    const envOk = hasSupabaseEnv();
    if (!envOk) {
      this.useDevFallback();
      this.initialized = true;
      return;
    }
    try {
      const { data: locationData, error } = await supabaseClient
        .from('population')
        .select('location_name, county, geography_type')
        .eq('year', 2023)
        .not('location_name', 'is', null)
        .not('geography_type', 'is', null);

      if (error) {
        console.error('Error fetching location data from Supabase:', error);
        throw error;
      }

      if (locationData && locationData.length > 0) {
        this.buildLocationMaps(locationData);
      } else {
        throw new Error('No location data available');
      }

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Supabase location service:', error);
      this.useDevFallback();
      this.initialized = true;
    }
  }



  private buildLocationMaps(locationData: any[]) {
    // Build unique locations list
    const uniqueLocations = new Map<string, LocationInfo>();
    
    locationData.forEach(row => {
      const locationName = row.location_name?.trim();
      const county = row.county?.trim();
      const geographyType = row.geography_type?.trim();
      
      if (locationName && geographyType && !uniqueLocations.has(locationName)) {
        // Map geography_type to our type system
        let type: 'city' | 'county' | 'state';
        
        if (geographyType === 'State') {
          type = 'state';
        } else if (geographyType === 'County') {
          type = 'county';
        } else if (geographyType === 'City') {
          type = 'city';
        } else {
          // Fallback to name-based detection for backward compatibility
          if (locationName === 'California') {
            type = 'state';
          } else if (locationName.endsWith(' County')) {
            type = 'county';
          } else {
            type = 'city';
          }
        }
        
        uniqueLocations.set(locationName, {
          name: locationName,
          county: county || '',
          type
        });
      }
    });

    this.locations = Array.from(uniqueLocations.values());
    this.slugMap.clear();
    this.locations.forEach((loc) => {
      const slug = this.buildLocationSlug(loc);
      if (this.slugMap.has(slug)) {
        return;
      }
      this.slugMap.set(slug, loc);
    });

    // Build city to county mapping
    locationData.forEach(row => {
      const locationName = row.location_name?.trim();
      const county = row.county?.trim();
      const geographyType = row.geography_type?.trim();
      
      if (locationName && county && geographyType === 'City') {
        this.cityCountyMap.set(locationName, county);
      }
    });
  }

  getAvailableLocations(): LocationInfo[] {
    if (!this.initialized) {
      return [];
    }
    if (this.locations.length === 0) {
      return [...DEV_FALLBACK_LOCATIONS];
    }
    return this.locations;
  }

  /** Resolve slug without console noise when uninitialized (routing before `initialize()` completes). */
  peekLocationBySlug(slug: string): LocationInfo | null {
    if (!this.initialized || !slug?.trim()) return null;
    const normalized = SupabaseLocationService.slugify(slug);
    return this.slugMap.get(normalized) || null;
  }

  getLocationBySlug(slug: string): LocationInfo | null {
    if (!this.initialized) {
      return null;
    }
    const normalized = SupabaseLocationService.slugify(slug);
    return this.slugMap.get(normalized) || null;
  }

  getSlugForLocation(locationName: string): string | null {
    if (!this.initialized) {
      return null;
    }
    const match = this.locations.find((loc) => loc.name === locationName);
    if (!match) return null;
    return this.buildLocationSlug(match);
  }

  getCities(): LocationInfo[] {
    if (!this.initialized) {
      return [];
    }
    return this.locations.filter(loc => loc.type === 'city');
  }

  getCounties(): LocationInfo[] {
    if (!this.initialized) {
      return [];
    }
    return this.locations.filter(loc => loc.type === 'county');
  }

  /** Cities in a county (California); county name should match DB e.g. "Yolo County". */
  getCitiesInCounty(countyName: string): string[] {
    if (!this.initialized || !countyName?.trim()) return [];
    const county = countyName.trim();
    return this.locations
      .filter((loc) => loc.type === 'city' && loc.county?.trim() === county)
      .map((loc) => loc.name);
  }

  /** Cities in county sorted by latest population descending (most populous first). */
  async getCitiesInCountySortedByPopulation(countyName: string): Promise<string[]> {
    const cities = this.getCitiesInCounty(countyName);
    if (cities.length === 0) return [];
    const withPop = await Promise.all(
      cities.map(async (name) => ({ name, pop: (await this.getLatestPopulation(name))?.population ?? 0 }))
    );
    withPop.sort((a, b) => b.pop - a.pop);
    return withPop.map((x) => x.name);
  }

  getCountyForCity(cityName: string): string | null {
    if (!this.initialized) {
      return null;
    }
    return this.cityCountyMap.get(cityName) || null;
  }

  getLocationType(locationName: string): 'city' | 'county' | 'state' | null {
    if (!this.initialized) return null;
    const match = this.locations.find((loc) => loc.name === locationName);
    return match?.type ?? null;
  }

  searchLocations(query: string): LocationInfo[] {
    if (!this.initialized) {
      return [];
    }
    
    const lowerQuery = query.toLowerCase();
    const results = this.locations.filter(loc => 
      loc.name.toLowerCase().includes(lowerQuery) ||
      loc.county.toLowerCase().includes(lowerQuery)
    );
    
    
    return results;
  }

  private buildLocationSlug(location: LocationInfo): string {
    const trimmed = location.name.trim();
    if (location.type === 'state') {
      return SupabaseLocationService.slugify(trimmed);
    }

    if (location.type === 'county') {
      const base = trimmed.replace(/\s+County$/i, '');
      return `${SupabaseLocationService.slugify(base)}-county`;
    }

    return SupabaseLocationService.slugify(trimmed);
  }

  private static slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/--+/g, '-');
  }

  // Get population data with proper Supabase filtering using normalized table
  async getPopulationData(locationName: string, year?: number): Promise<PopulationDataRow[]> {
    try {
      let query = supabaseClient
        .from('population')
        .select('location_name, county, year, population')
        .eq('location_name', locationName);

      if (year) {
        query = query.eq('year', year);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching population data from Supabase:', error);
        throw error;
      }


      if (!data || data.length === 0) {
        return [];
      }

      return data.map(row => ({
        location_name: row.location_name,
        county: row.county || '',
        population: row.population,
        year: row.year
      }));
    } catch (error) {
      console.error('Failed to fetch population data from Supabase:', error);
      throw error;
    }
  }

  // Get latest population with proper Supabase filtering using normalized table
  async getLatestPopulation(locationName: string): Promise<LatestPopulationData | null> {
    try {
      const { data, error } = await supabaseClient
        .from('population')
        .select('population, year')
        .eq('location_name', locationName)
        .not('population', 'is', null)
        .order('year', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching latest population from Supabase:', error);
        throw error;
      }


      if (!data || data.length === 0) {
        return null;
      }

      return data[0];
    } catch (error) {
      console.error('Failed to fetch latest population from Supabase:', error);
      throw error;
    }
  }

  /** Population rows for a place: exact name first, then names starting with the same string (e.g. "Berkeley" vs "Berkeley city"). */
  private async populationRowsForLocationName(locationName: string): Promise<Array<{ geofips: number; geography_type: string; location_name: string }>> {
    const trimmed = (locationName || '').trim();
    if (!trimmed) return [];
    const pick = (data: any[] | null) =>
      (data || []).filter((r) => r != null && r.geofips != null && r.geography_type != null) as Array<{
        geofips: number;
        geography_type: string;
        location_name: string;
      }>;

    const { data: exact, error: errExact } = await supabaseClient
      .from('population')
      .select('geofips, geography_type, location_name')
      .eq('location_name', trimmed)
      .order('geography_type', { ascending: true });
    if (errExact) {
      console.error('Error fetching geofips from Supabase:', errExact);
      return [];
    }
    if (exact?.length) return pick(exact);

    const { data: prefix, error: errPrefix } = await supabaseClient
      .from('population')
      .select('geofips, geography_type, location_name')
      .ilike('location_name', `${trimmed}%`)
      .order('geography_type', { ascending: true })
      .limit(40);
    if (errPrefix) {
      console.error('Error fetching geofips (prefix match) from Supabase:', errPrefix);
      return [];
    }
    return pick(prefix);
  }

  // Get geofips for a location
  async getGeofips(locationName: string): Promise<number | null> {
    if (this.devFallback && locationName in DEV_FALLBACK_GEOFIPS) {
      return DEV_FALLBACK_GEOFIPS[locationName] ?? null;
    }
    try {
      const allMatches = await this.populationRowsForLocationName(locationName);
      if (allMatches.length > 0) {
        const cityMatch = allMatches.find((m) => m.geography_type === 'City');
        const countyMatch = allMatches.find((m) => m.geography_type === 'County');
        if (cityMatch) return cityMatch.geofips;
        if (countyMatch) return countyMatch.geofips;
        return allMatches[0].geofips;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch geofips from Supabase:', error);
      return null;
    }
  }

  /**
   * Same disambiguation as getGeofips (City preferred over County), plus geography_type
   * for filtering elec_*_format / ng_*_format rows.
   */
  async getGeofipsWithGeographyType(
    locationName: string
  ): Promise<{ geofips: number; geography_type: string } | null> {
    try {
      const allMatches = await this.populationRowsForLocationName(locationName);
      if (allMatches.length > 0) {
        const cityMatch = allMatches.find((m) => m.geography_type === 'City');
        const countyMatch = allMatches.find((m) => m.geography_type === 'County');
        const pick = cityMatch || countyMatch || allMatches[0];
        const gt = pick.geography_type;
        if (pick.geofips != null && gt) {
          return { geofips: pick.geofips, geography_type: String(gt) };
        }
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch geofips/geography_type from Supabase:', error);
      return null;
    }
  }

  isUsingSupabase(): boolean {
    return this.initialized;
  }

  /** True when using dev fallback (no/invalid .env or DB error). Show "connect to database" hint. */
  isDevFallback(): boolean {
    return this.devFallback;
  }

  // Method to validate that no CSV loading is happening
  validateNoCSVLoading(): void {
    if (!this.initialized) {
      throw new Error('❌ Location service must be initialized with Supabase before use');
    }
    
    // Additional validation that we're not accidentally loading files
    const stack = new Error().stack;
    if (stack && stack.includes('fetch') && stack.includes('.csv')) {
      throw new Error('❌ CSV loading detected! All data must come from Supabase with proper filtering.');
    }
  }
}

export const supabaseLocationService = new SupabaseLocationService();
